import { useEffect, useState, type RefObject } from "react";

/**
 * How to draw a whole day in whatever width there is.
 *
 * ## The problem this replaces
 *
 * The strip used a fixed 54px per hour, so a 24-hour day was always 1296px.
 * On any container narrower than that - a laptop, a browser at 125%, a window
 * dragged in - it simply scrolled, and zooming changed only how much of a
 * fixed-size thing you could see rather than re-laying it out. A day view that
 * cannot show the day is not a day view.
 *
 * ## What decides the floor
 *
 * Not the hour, the LABEL. "23.00" needs about 46px to sit next to its
 * neighbour without touching, and that is the only reason an hour column had a
 * minimum at all. So instead of forcing the strip wide enough for 24 labels,
 * the labels thin out: every hour, every second, every third, every sixth. The
 * day always fits; the ruler stays readable; and zooming re-flows because the
 * measurement is of real layout rather than a constant.
 */

/** Label steps, in hours, coarsest last. */
const STEPS = [1, 2, 3, 4, 6, 12];
/** Roughly the width of "23.00" plus the air it needs. */
const LABEL_WIDTH = 46;

/**
 * The narrowest an hour column may be drawn.
 *
 * With labels thinning out, this only has to keep the GRID legible - below
 * this a day is a grey smear and scrolling is genuinely better than
 * compressing further.
 */
export const MIN_HOUR_WIDTH = 22;

/** Observed width of `ref`, in CSS pixels. Null until first measured. */
export function useElementWidth(
  ref: RefObject<HTMLElement | null>
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // ResizeObserver rather than a window listener: browser zoom, a collapsing
    // sidebar and a dragged window all change this element's width, and only
    // one of those is a window resize event.
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}

/**
 * How many hours between printed labels, given the room available.
 *
 * Falls back to every hour before the first measurement - the strip renders
 * once with a dense ruler rather than with none, which is the less startling
 * of the two on a slow first paint.
 */
export function hourLabelStep(
  containerWidth: number | null,
  hours: number
): number {
  if (!containerWidth || hours <= 0) return 1;
  const perHour = containerWidth / hours;
  for (const step of STEPS) {
    if (perHour * step >= LABEL_WIDTH) return step;
  }
  return STEPS[STEPS.length - 1]!;
}

/* -------------------------------------------------------------------------- */
/* Card grids                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How many columns a `repeat(auto-fill, minmax(min, 1fr))` grid will make.
 *
 * `auto-fill`'s own arithmetic, restated so JS can agree with the CSS: n
 * columns need `n * min + (n - 1) * gap` and no more than the track's width,
 * which rearranges to the floor below.
 *
 * It exists because a caller has to know the answer BEFORE rendering - a row
 * that shows "one row's worth" of cards cannot slice its list until it knows
 * how wide a row is. The Channels page used a fixed six, so on a wide window
 * the grid offered nine columns, six were filled, and the row stopped visibly
 * short of a header that ran to the full width.
 *
 * `null` width means "not measured yet"; the caller supplies the fallback,
 * because what to show for one frame is a design decision and not arithmetic.
 */
export function columnsThatFit(
  width: number | null,
  min: number,
  gap: number,
  unmeasured: number
): number {
  if (!width) return unmeasured;
  return Math.max(1, Math.floor((width + gap) / (min + gap)));
}
